<template>
	<section class="home-view">
		<PrimeDataTable
			v-if="images.length > 0 && !isDataLoading"
			:value="images"
			style="width: 100%"
			resizableColumns
			showGridlines
			stripedRows
			scrollable
			scrollHeight="calc(100vh - 184px)"
		>
			<PrimeColumn field="image" header="Image">
				<template #body="slotProps">
					<PrimeSkeleton v-if="!slotProps.data.isLoaded" width="64px" height="64px" />
					<PrimeImage
						v-show="slotProps.data.isLoaded"
						:src="`https://pculwozsgokcvecdekjo.supabase.co/functions/v1/cors-proxy?url=${slotProps.data.url}`"
						:alt="slotProps.data.url"
						width="64px"
						height="64px"
						preview
						@load="onImageLoaded($event, slotProps.data)"
					/>
				</template>
			</PrimeColumn>
			<PrimeColumn field="customer_email" header="Customer Email" />
			<PrimeColumn field="created_at" header="Added at" />
			<PrimeColumn field="is_alive" header="URL Alive" bodyStyle="text-align:center">
				<template #body="slotProps">
					<i
						v-if="slotProps.data.is_alive"
						class="pi pi-check"
						style="font-size: 1rem"
					></i>
					<i v-else class="pi pi-times" style="font-size: 1rem"></i>
				</template>
			</PrimeColumn>
			<PrimeColumn header="Data Amount (Valid / Total)">
				<template #body="slotProps">
					<PrimeProgressBar
						v-if="slotProps.data.dataValid > 0"
						:value="
							(slotProps.data.dataValid / MAX_COLOR_DATA_PROPS_COUNT > 1
								? 1
								: slotProps.data.dataValid / MAX_COLOR_DATA_PROPS_COUNT) * 100
						"
					>
						{{ slotProps.data.dataValid }} / {{ slotProps.data.dataTotal }}
					</PrimeProgressBar>
					<p v-else-if="slotProps.data.dataValid === undefined">
						<PrimeSkeleton width="100%" height="var(--p-progressbar-height)" />
					</p>
					<p v-else>-</p>
				</template>
			</PrimeColumn>
			<PrimeColumn field="is_nsfw" header="NSFW" bodyStyle="text-align:center">
				<template #body="slotProps">
					<p v-if="slotProps.data.nsfw_check_at == '-'">-</p>
					<i
						v-else-if="slotProps.data.is_nsfw"
						class="pi pi-check"
						style="font-size: 1rem"
					></i>
					<i v-else class="pi pi-times" style="font-size: 1rem"></i>
				</template>
			</PrimeColumn>
			<PrimeColumn
				field="is_in_gallery"
				header="Live in gallery"
				bodyStyle="text-align:center"
			>
				<template #body="slotProps">
					<i
						v-if="slotProps.data.is_in_gallery"
						class="pi pi-check"
						style="font-size: 1rem"
					></i>
					<i v-else class="pi pi-times" style="font-size: 1rem"></i>
				</template>
			</PrimeColumn>
			<PrimeColumn field="last_alive_check_at" header="Last URL Check"></PrimeColumn>
			<PrimeColumn
				field="is_force_hidden"
				header="Force hidden"
				bodyStyle="text-align:center"
			>
				<template #body="slotProps">
					<PrimeToggleButton
						v-model="slotProps.data.is_force_hidden"
						onIcon="pi pi-check"
						offIcon="pi pi-times"
						:invalid="!slotProps.data.is_force_hidden"
						@change="toggleImage(slotProps.data)"
					/>
				</template>
			</PrimeColumn>
		</PrimeDataTable>

		<div v-else style="margin-top: 30vh">
			<i class="pi pi-spin pi-spinner" style="font-size: 4rem"></i>
		</div>

		<div v-if="isPaginatorDisplayed" class="flex flex-row">
			<PrimePaginator
				:rows="IMAGES_PER_PAGE"
				:totalRecords="totalImagesCount"
				:template="{
					default:
						'FirstPageLink PrevPageLink JumpToPageDropdown NextPageLink LastPageLink'
				}"
				@page="onPageChanged"
				:first="(currentPage - 1) * IMAGES_PER_PAGE"
			/>
		</div>
	</section>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted, watch } from "vue";
import { computedAsync, useTimeAgo } from "@vueuse/core";

import PrimeDataTable from "primevue/datatable";
import PrimeColumn from "primevue/column";
import PrimeImage from "primevue/image";
import PrimeToggleButton from "primevue/togglebutton";
import PrimePaginator from "primevue/paginator";
import PrimeProgressBar from "primevue/progressbar";
import PrimeSkeleton from "primevue/skeleton";

import { PrimeIcons } from "@primevue/core/api";

import { useToast } from "primevue/usetoast";
import { useRouter } from "vue-router";

import { supabase } from "@/ts/supabase";
import { useRoute } from "vue-router";

import exifr from "exifr-webp";

import { formattedXmpDataKeys } from "@/ts/schemas/xmpLightroomFormatted";

const IMAGES_PER_PAGE = 25;
const MAX_COLOR_DATA_PROPS_COUNT = 40;

type GoodhueImage = {
	url: string;
	customer_email: string | null;
	created_at: string;
	id: string;
	is_alive: boolean;
	last_alive_check_at: string | null;
	is_nsfw: boolean;
	nsfw_check_at: string | null;
	is_force_hidden: boolean;
	is_in_gallery: boolean;

	// for data amount
	dataTotal?: number;
	dataValid?: number;

	// for skeleton
	isLoaded: boolean;
};

export default defineComponent({
	name: "HomeView",
	components: {
		PrimeDataTable,
		PrimeColumn,
		PrimeImage,
		PrimeToggleButton,
		PrimePaginator,
		PrimeProgressBar,
		PrimeSkeleton
	},

	setup() {
		const toast = useToast();
		const userEmail = computedAsync(async () => {
			const { data, error } = await supabase.auth.getUser();

			return error ? "..." : data.user?.email;
		}, undefined);

		const images = ref<GoodhueImage[]>([]);

		const route = useRoute();
		const router = useRouter();

		const totalImagesCount = ref(0);

		const isDataLoading = ref(true);
		const isPaginatorDisplayed = computed(() => {
			return (
				images.value.length > 0 &&
				totalImagesCount.value > IMAGES_PER_PAGE &&
				!isDataLoading.value
			);
		});

		//

		const currentPage = computed(() => Math.floor(parseInt((route.query.page as any) || 1)));

		const menubarItems = ref([
			{
				label: "Statistics",
				icon: PrimeIcons.CHART_BAR,
				route: "/stats"
			},
			{
				label: "Gallery Preview",
				icon: PrimeIcons.IMAGES,
				route: "/gallery-preview"
			}
		]);
		const userButtonItems = ref([
			{
				label: "Logout",
				command: logout
			}
		]);

		//

		async function logout() {
			const { error } = await supabase.auth.signOut({ scope: "local" });

			if (!error) {
				await router.push("login");
			} else {
				toast.add({
					summary: "Unable to logout",
					detail: error.message,
					severity: "error"
				});
			}
		}

		async function fetchImages(): Promise<GoodhueImage[] | undefined> {
			const from = (currentPage.value - 1) * IMAGES_PER_PAGE;
			const to = from + IMAGES_PER_PAGE - 1;

			totalImagesCount.value = 0;
			images.value = [];

			isDataLoading.value = true;

			try {
				const { data, count, error } = await supabase
					.from("images")
					.select("*, images_gallery(image_id, added_at, is_hidden)", { count: "exact" })
					.range(from, to)
					.order("created_at", { ascending: false });

				if (error) {
					toast.add({
						summary: "Unable to fetch images from Supabase",
						detail: error.message,
						severity: "error"
					});

					return;
				}

				if (data.length == 0 && currentPage.value !== 1) {
					toast.add({
						summary: "No data for this page",
						detail: "Heading you to home page in 3 seconds...",
						severity: "warn",
						life: 3000
					});

					setTimeout(() => {
						router.push("/");
					}, 3000);

					return;
				}

				totalImagesCount.value = count || 0;

				images.value = data.map((entry) => {
					return {
						url: entry.url,
						customer_id: entry.customer_id,
						customer_email: entry.customer_email?.toLowerCase() || "-",
						created_at: useTimeAgo(new Date(entry.created_at)).value,
						id: entry.id,
						is_alive: entry.is_alive,
						last_alive_check_at:
							entry.last_alive_check_at == undefined
								? "-"
								: useTimeAgo(new Date(entry.last_alive_check_at)).value,
						is_nsfw: entry.is_nsfw,
						nsfw_check_at:
							entry.nsfw_check_at == undefined
								? "-"
								: useTimeAgo(new Date(entry.nsfw_check_at)).value,
						is_force_hidden: entry.images_gallery[0]?.is_hidden ?? null,
						is_in_gallery:
							entry.images_gallery.length > 0 &&
							entry.images_gallery[0]?.is_hidden !== true,

						dataValid: undefined,
						dataTotal: undefined,

						isLoaded: false
					};
				});
			} finally {
				isDataLoading.value = false;
			}
		}

		async function toggleImage(image: GoodhueImage) {
			if (image.is_force_hidden) {
				if (!image.is_in_gallery) {
					const { error } = await supabase.from("images_gallery").insert({
						image_id: image.id,
						is_hidden: true
					});

					if (error) {
						console.warn("Unable to hide the image", error);
						image.is_force_hidden = false;
					}
				} else {
					const { error } = await supabase
						.from("images_gallery")
						.update({ is_hidden: true })
						.eq("image_id", image.id);

					if (error) {
						console.warn("Unable to hide (update the flag) the image", error);
						image.is_force_hidden = false;
					}
				}
			} else {
				const { error } = await supabase
					.from("images_gallery")
					.update({ is_hidden: false })
					.eq("image_id", image.id);

				if (error) {
					console.warn("Unable to remove hidden flag from the image", error);
					image.is_force_hidden = true;
				}
			}
		}

		async function onImageLoaded(
			event: Event,
			slotPropsData: { dataTotal: number; dataValid: number; isLoaded: boolean }
		) {
			try {
				const response = await fetch((event.target as HTMLImageElement).src, {
					cache: "force-cache"
				});
				const imageData = await response.arrayBuffer();

				const xmpData = await exifr.parse(imageData, {
					xmp: {
						parse: true
					}
				});

				let propsCounter = 0;
				const xmpKeys = Object.keys(xmpData);

				Object.keys(formattedXmpDataKeys).forEach((propName: string) => {
					if (xmpKeys.includes(propName)) {
						const value = (xmpData as Record<string, any>)[propName];

						const valueType = (formattedXmpDataKeys as Record<string, any>)[propName];

						if (typeof value == "number" && value == 0) {
							return;
						}

						if (typeof value == valueType) {
							propsCounter++;
						}
					}
				});

				slotPropsData.dataValid = propsCounter;
				slotPropsData.dataTotal = xmpKeys.length;
			} catch (ex) {
			} finally {
				slotPropsData.isLoaded = true;
			}
		}

		async function onPageChanged(e: { page: number }) {
			isDataLoading.value = true;

			await router.push({
				name: "home",
				query: {
					page: String(e.page + 1)
				}
			});
		}

		//

		watch(
			() => route.query.page,
			() => {
				fetchImages();
			},
			{ immediate: true }
		);

		onMounted(async () => {
			fetchImages();
		});

		return {
			IMAGES_PER_PAGE,
			MAX_COLOR_DATA_PROPS_COUNT,

			userEmail,
			images,
			totalImagesCount,
			menubarItems,
			userButtonItems,
			currentPage,

			isDataLoading,
			isPaginatorDisplayed,

			logout,
			toggleImage,
			onImageLoaded,
			onPageChanged
		};
	}
});
</script>
<style lang="scss" scoped>
.home-view {
	display: flex;
	flex-direction: column;
	justify-content: flex-start;
	align-items: center;
}
</style>
